import React, { FC, useRef, useState } from 'react';
import { useButton, useI18n, useLicenses, useLoyalty, useSelector } from '~Hooks';
import { Wrapper, ListWapper, List } from '../PrintBill/styles';
import ChangeChequeOwner from './ChangeChequeOwner';
import RenameCheque from './RenameCheque';
import MoreVertIcon from '~/Assets/icons/more_vert.svg';
import PersonIcon from '~Assets/icons/person.svg';
import EditIcon from '~/Assets/icons/create.svg';
import PersonEditIcon from '~Assets/icons/person-edit.svg';
import SwapIcon from '~/Assets/icons/swap.svg';
import TableButton from '../../TableButton';
import useModal from '~ComponentsImproved/Modal/useModal';
import LinkUser from '~Components/LinkUser';
import { useTableUniverse } from '~Components/TableUniverse';
import styled from '@emotion/styled';
import { WhiteButton } from '~Styles';
import Icon from '~ComponentsImproved/Icon';
import { useStorage } from '~Components/Storage';
import LoyaltyModal, { ContactlessIcon } from '~Components/LoyaltyModal';

const ButtonWrapper = styled.div`
  margin-left: 8px;
  flex: 1;
  max-width: 200px;
`;

interface Props {
  selectedCheque: string | null;
  showLogin: () => void;
}

/**
 * If venue has App - Render this component instead for:
 * - Rename Cheque
 * - Change owner of Cheque
 * - Set user to cheque
 */
const EditChequeActions: FC<Props> = ({ selectedCheque, showLogin }) => {
  const { i18n } = useI18n();
  const linkCustomerModal = useModal();
  const changeOwnerModal = useModal();
  const renameChequeModal = useModal();
  const universe = useTableUniverse();
  const licenses = useLicenses();
  const loyalty = useLoyalty();
  const [storage] = useStorage();
  const terminalId = storage.adyenTerminals[0]?.POIID;

  const country = useSelector((state) => state.country);
  const staffId = useSelector((state) =>
    state.companyStaff?.staffs.length ? state.staff : false,
  );

  const cheque = universe.cheques.find((c) => c.id === selectedCheque);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const openList = () => setIsOpen(true);
  const closeList = () => setIsOpen(false);

  const ref = useRef<any>();

  const showIcon = window.innerWidth > 800;

  const handleTouchEnd = () => {
    openList();
  };

  const memberButton = useButton({
    onClick: () => {
      closeList();
      linkCustomerModal.show();
    },
  });

  const loyaltyButton = useButton({
    onClick: () => {
      closeList();
      loyalty.loyaltyModal.show();
    },
    disabled: loyalty.isWaitingForCard,
  });

  const changeOwnerButton = useButton({
    onClick: () => {
      closeList();
      changeOwnerModal.show();
    },
  });

  const renameChequeButton = useButton({
    onClick: () => {
      closeList();
      staffId !== null ? renameChequeModal.show() : showLogin();
    },
  });

  return (
    <>
      {country === 'ES' || licenses.hasLoyaltyPro ? (
        <Wrapper ref={ref}>
          {isOpen && (
            <ListWapper>
              <List>
                {country === 'ES' && (
                  <WhiteButton display="flex" alignItems="center" gap="xs" {...memberButton}>
                    {i18n('CustomerForCheckout.Member')}
                    {showIcon && (
                      <Icon
                        src={cheque?.userId ? PersonEditIcon : PersonIcon}
                        color="var(--colorBlueGray900)"
                      />
                    )}
                  </WhiteButton>
                )}

                {licenses.hasLoyaltyPro && (
                  <WhiteButton display="flex" alignItems="center" gap="xs" {...loyaltyButton}>
                    {i18n('Loyalty.Title')}
                    {showIcon &&
                      (loyalty.isWaitingForCard ? (
                        <ContactlessIcon size={20} animated />
                      ) : (
                        <Icon
                          src={cheque?.userId ? PersonEditIcon : PersonIcon}
                          color="var(--colorBlueGray900)"
                        />
                      ))}
                  </WhiteButton>
                )}

                <WhiteButton display="flex" alignItems="center" gap="xs" {...changeOwnerButton}>
                  {i18n('TM.ChangeOwner')}
                  {showIcon && <Icon src={SwapIcon} color="var(--colorBlueGray900)" />}
                </WhiteButton>

                <WhiteButton display="flex" alignItems="center" gap="xs" {...renameChequeButton}>
                  {i18n('TM.Rename')}
                  {showIcon && <Icon src={EditIcon} color="var(--colorBlueGray900)" />}
                </WhiteButton>
              </List>

              <TableButton onTouchStart={closeList} color="var(--colorUIRed)">
                {i18n('TM.Close')}
              </TableButton>
            </ListWapper>
          )}

          <TableButton icon={showIcon && MoreVertIcon} onTouchEnd={handleTouchEnd}>
            {i18n('TM.EditCheque')}
          </TableButton>
        </Wrapper>
      ) : (
        <>
          <ButtonWrapper
            onTouchStart={() => (staffId !== null ? renameChequeModal.show() : showLogin())}>
            <TableButton icon={showIcon && EditIcon}>{i18n('TM.Rename')}</TableButton>
          </ButtonWrapper>
          {staffId && (
            <ButtonWrapper onTouchStart={changeOwnerModal.show}>
              <TableButton icon={showIcon && SwapIcon}>{i18n('TM.ChangeOwner')}</TableButton>
            </ButtonWrapper>
          )}
        </>
      )}

      <LinkUser
        isShowing={linkCustomerModal.isShowing}
        onClose={linkCustomerModal.hide}
        selectedCheque={selectedCheque}
        selectedChequeUserId={cheque?.userId || null}
      />

      <LoyaltyModal
        isShowing={loyalty.loyaltyModal.isShowing}
        hide={loyalty.loyaltyModal.hide}
        terminalId={terminalId}
        pendingCard={loyalty.pendingCard}
        pendingError={loyalty.pendingError}
        onRequestTapCard={() => loyalty.waitForCard(terminalId)}
        onClearPending={loyalty.clearPending}
        isTsr
        selectedCheque={selectedCheque}
        selectedChequeUserId={cheque?.userId || null}
      />

      <ChangeChequeOwner
        isShowing={changeOwnerModal.isShowing}
        onClose={changeOwnerModal.hide}
        selectedCheque={selectedCheque}
      />

      <RenameCheque
        isShowing={renameChequeModal.isShowing}
        onClose={renameChequeModal.hide}
        selectedCheque={selectedCheque}
      />
    </>
  );
};

export default EditChequeActions;
